// The phone frame, the layer that covers it, toasts, confirm sheets, icons, the waveform, sliders, time formatting.
import { createPortal } from "react-dom";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// The phone mock-up. Every screen fills it and scrolls its own body; the layer on top holds the screens opened
// over it, sheets and toasts.
const PhoneLayer = createContext<HTMLElement | null>(null);
export function Phone({ children }: { children: ReactNode }) {
  const [layer, setLayer] = useState<HTMLElement | null>(null);
  return (
    <div className="phone">
      <PhoneLayer.Provider value={layer}>
        <div className="phone-screen">{children}</div>
      </PhoneLayer.Provider>
      <div className="phone-layer" ref={setLayer} />
    </div>
  );
}
// Renders into the top layer of the phone it is in (or in place before that exists). Each phone has its own, so several can share a page (the dev canvas).
export function OnTop({ children }: { children: ReactNode }) {
  const layer = useContext(PhoneLayer);
  return layer ? createPortal(children, layer) : <>{children}</>;
}
// A message at the bottom. With onClick it also goes away by itself after a few seconds.
export function Toast({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  useEffect(() => {
    if (!onClick) return;
    const t = setTimeout(onClick, 4500);
    return () => clearTimeout(t);
  }, [children]);
  return <OnTop><div className="toast" onClick={onClick}>{children}</div></OnTop>;
}

// A small sheet from the bottom, for questions like "Delete this?".
function Sheet({ onClose, children, title }: { onClose: () => void; children: ReactNode; title?: ReactNode }) {
  return (
    <OnTop>
      <div className="sheet-wrap" onClick={onClose}>
        <div className="sheet" onClick={e => e.stopPropagation()}>
          <div className="sheet-grab" />
          {title && <div className="sheet-title">{title}</div>}
          {children}
        </div>
      </div>
    </OnTop>
  );
}

export function Confirm({ title, children, yes, onYes, onNo, danger }: { title: ReactNode; children?: ReactNode; yes: string; onYes: () => void; onNo: () => void; danger?: boolean }) {
  return (
    <Sheet onClose={onNo} title={title}>
      {children && <p className="hint">{children}</p>}
      <div className="sheet-actions">
        <button className="btn" onClick={onNo}>Cancel</button>
        <button className={`btn${danger ? " danger" : " yellow"}`} onClick={onYes}>{yes}</button>
      </div>
    </Sheet>
  );
}

export function Slider({ label, value, min, max, step = 1, onChange, left, right, show, disabled }: {
  label: string; value: number; min: number; max: number; step?: number; onChange: (v: number) => void;
  left?: ReactNode; right?: ReactNode; show?: (v: number) => string; disabled?: boolean;
}) {
  const p = ((value - min) / (max - min)) * 100;
  return (
    <label className={`slider${disabled ? " disabled" : ""}`}>
      <span className="slider-top"><b>{label}</b><span className="muted">{show ? show(value) : ""}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} disabled={disabled} aria-label={label}
        style={{ ["--p" as string]: `${p}%` }} onChange={e => onChange(Number(e.target.value))} />
      {(left || right) && <span className="slider-ends muted"><span>{left}</span><span>{right}</span></span>}
    </label>
  );
}

// A row of options where one is picked.
export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: [T, ReactNode][]; onChange: (v: T) => void; label?: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => <button key={v} role="radio" aria-checked={value === v} className={value === v ? "on" : ""} onClick={() => onChange(v)}>{l}</button>)}
    </div>
  );
}

export const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
export const secs = (d: number) => `${d.toFixed(1)}s`;
export const cap = (s: string) => s[0]!.toUpperCase() + s.slice(1);

const paths: Record<string, ReactNode> = {
  back: <path d="M15 5l-7 7 7 7" />,
  chevron: <path d="M9 5l7 7-7 7" />,
  play: <path d="M8 5v14l11-7z" fill="currentColor" />,
  stop: <rect x={6} y={6} width={12} height={12} rx={2} fill="currentColor" />,
  shuffle: <path d="M3 7h3c5 0 7 10 12 10h3M3 17h3c2 0 3.5-1.5 4.5-3.5M14 9.5C15 8 16 7 18 7h3M18 4l3 3-3 3M18 14l3 3-3 3" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  replay: <path d="M4 12a8 8 0 108-8H8M8 1L5 4l3 3" />,
  expand: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  up: <path d="M12 19V5M6 11l6-6 6 6" />,
  down: <path d="M12 5v14M6 13l6 6 6-6" />,
  trash: <path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />,
  video: <><rect x={2.5} y={6} width={13} height={12} rx={2.5} /><path d="M15.5 10.5l6-3.5v10l-6-3.5" /></>,
  mic: <><rect x={9} y={3} width={6} height={11} rx={3} /><path d="M6 11a6 6 0 0012 0M12 17v4" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  "zoom-in": <><circle cx={10.5} cy={10.5} r={6.5} /><path d="M15.5 15.5L21 21M10.5 7.5v6M7.5 10.5h6" /></>,
  "zoom-out": <><circle cx={10.5} cy={10.5} r={6.5} /><path d="M15.5 15.5L21 21M7.5 10.5h6" /></>,
  keyboard: <><rect x={3} y={6} width={18} height={12} rx={2} /><path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10" /></>,
  download: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />,
  people: <><circle cx={9} cy={8} r={3.5} /><path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 4.8a3.5 3.5 0 010 6.4M18 14.4c2.2.7 3.5 2.8 3.5 5.6" /></>,
  person: <><circle cx={12} cy={6} r={3} /><path d="M12 9v7M8 21l4-5 4 5M7 12h10" /></>,
  sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.7 1.8 1.8.7-1.8.7L19 21l-.7-1.8-1.8-.7 1.8-.7z" />,
  more: <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth="3.5" />,
  undo: <path d="M9 14L4 9l5-5M4 9h10a6 6 0 010 12h-3" />,
  redo: <path d="M15 14l5-5-5-5M20 9H10a6 6 0 000 12h3" />,
  film: <><rect x={3} y={4} width={18} height={16} rx={2} /><path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" /></>,
  mirror: <path d="M12 3v18M9 6L3 12l6 6zM15 6l6 6-6 6" />,
  face: <><circle cx={12} cy={12} r={9} /><path d="M8.5 14.5c1.8 2 5.2 2 7 0M9 9.5h.01M15 9.5h.01" /></>,
  eye: <><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12z" /><circle cx={12} cy={12} r={2.6} /></>,
  hand: <path d="M8 13V5.5a1.5 1.5 0 013 0V11M11 10V4a1.5 1.5 0 013 0v6M14 10V5.5a1.5 1.5 0 013 0V14c0 4-2.5 7-6 7-2.6 0-4-1.4-5.5-3.6L3.8 15a1.5 1.5 0 012.4-1.8L8 15" />,
  image: <><rect x={3} y={4} width={18} height={16} rx={2} /><circle cx={9} cy={10} r={2} /><path d="M21 16l-5-5-9 9" /></>,
  voice: <path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2" />,
  silence: <path d="M3 12h2M8 12h2M13 12h2M18 12h3" />, // a flat sound wave
  move: <path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" />,
  lock: <><rect x={5} y={11} width={14} height={9} rx={2} /><path d="M8 11V8a4 4 0 018 0v3" /></>,
  pause: <><rect x={6} y={5} width={4} height={14} rx={1} fill="currentColor" /><rect x={14} y={5} width={4} height={14} rx={1} fill="currentColor" /></>,
};
export function Icon({ name, size = 22 }: { name: keyof typeof paths | string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths[name]}
    </svg>
  );
}
